// Hero live iframe — remplace la vignette statique "promise" par une
// iframe live sur iframe.geovinum.app, avec une animation de typing qui
// cycle sur des appellations célèbres (Chablis, Meursault, Pomerol,
// Barolo, Rioja, Champagne). L'iframe se recharge avec `?app=` à la
// fin de chaque cycle pour montrer la vraie carte.
//
// Placé DANS le Mac browser mockup pour cohérence visuelle avec les
// autres slides desktop.
import { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import { apiFetch } from "@/lib/api";

const APPELLATIONS = ["Chablis", "Meursault", "Pomerol", "Barolo", "Rioja", "Champagne"];
const IFRAME_BASE = "https://iframe.geovinum.app/";
const TYPE_MS = 90;          // per-character typing speed
const HOLD_MS = 2500;         // hold after full word before moving to next
const ERASE_MS = 45;          // per-character erase speed

const HeroLiveIframe = ({ testId }) => {
  const [token, setToken] = useState(null);
  const [tokenFailed, setTokenFailed] = useState(false);
  const [wordIdx, setWordIdx] = useState(0);
  const [displayed, setDisplayed] = useState("");
  const [phase, setPhase] = useState("typing"); // typing → hold → erasing
  const [committedApp, setCommittedApp] = useState(""); // last appellation loaded in the iframe

  // ─── One-shot token fetch — shared with CoverImage via the same
  // /api/geovinum-token endpoint; safe to call again as the endpoint
  // caches server-side.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { ok, data } = await apiFetch("/api/geovinum-token");
      if (cancelled) return;
      if (ok && data?.token) setToken(data.token);
      else setTokenFailed(true);
    })();
    return () => { cancelled = true; };
  }, []);

  // ─── Typewriter loop driven by 3 phases:
  //   • typing:  add letters until we match the target word
  //   • hold:    freeze for HOLD_MS, then commit to iframe + start erasing
  //   • erasing: remove letters until empty, then move to next word
  useEffect(() => {
    const target = APPELLATIONS[wordIdx];
    let t;
    if (phase === "typing") {
      if (displayed.length < target.length) {
        t = setTimeout(() => setDisplayed(target.slice(0, displayed.length + 1)), TYPE_MS);
      } else {
        // Full word typed: commit to the iframe + move to hold phase.
        setCommittedApp(target);
        t = setTimeout(() => setPhase("hold"), 200);
      }
    } else if (phase === "hold") {
      t = setTimeout(() => setPhase("erasing"), HOLD_MS);
    } else if (phase === "erasing") {
      if (displayed.length > 0) {
        t = setTimeout(() => setDisplayed(displayed.slice(0, -1)), ERASE_MS);
      } else {
        setWordIdx((i) => (i + 1) % APPELLATIONS.length);
        setPhase("typing");
      }
    }
    return () => clearTimeout(t);
  }, [displayed, phase, wordIdx]);

  const src = token
    ? `${IFRAME_BASE}?token=${encodeURIComponent(token)}${committedApp ? `&app=${encodeURIComponent(committedApp)}` : ""}`
    : "";

  return (
    <div className="mx-auto max-w-[560px]" data-testid={testId}>
      <div className="rounded-[10px] overflow-hidden border border-[color:var(--gv-line)] shadow-[0_40px_80px_-30px_rgba(46,80,103,0.45)] bg-white">
        {/* Browser chrome */}
        <div className="flex items-center gap-1.5 px-3 py-2 bg-[color:var(--gv-mist)] border-b border-[color:var(--gv-line)]">
          <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
          <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
          <span className="w-3 h-3 rounded-full bg-[#28c840]" />
          <div className="flex-1 flex justify-center">
            <div className="px-3 py-0.5 rounded-full bg-white border border-[color:var(--gv-line)] text-[10.5px] text-[color:var(--gv-ink)]/55 tabular-nums">
              iframe.geovinum.app
            </div>
          </div>
        </div>

        {/* Faux search bar overlay + iframe below.
            The search field is decorative: it shows the typewriter while
            the real iframe below reacts by re-embedding the committed
            appellation each time a word is fully typed. */}
        <div className="relative bg-white" style={{ aspectRatio: "16 / 10" }}>
          {/* Fake search input at top of the iframe */}
          <div className="absolute top-3 left-3 right-3 z-10 pointer-events-none">
            <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] shadow-sm px-3 py-2 flex items-center gap-2">
              <Search size={14} className="text-[color:var(--gv-pink)]" />
              <span className="text-[13px] text-[color:var(--gv-ink)] font-medium tabular-nums" aria-live="polite">
                {displayed}
                <span className="inline-block w-[1px] h-3.5 align-middle bg-[color:var(--gv-pink)] ml-0.5 animate-pulse" />
              </span>
            </div>
          </div>

          {/* Real geoVINUM interactive map */}
          {token && !tokenFailed ? (
            <iframe
              src={src}
              title="Aperçu carte geoVINUM"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="absolute inset-0 w-full h-full"
              style={{ border: 0 }}
              data-testid={testId ? `${testId}-iframe` : undefined}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[color:var(--gv-mist)] to-white text-[color:var(--gv-ink)]/40 text-[13px]">
              Chargement de la carte…
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeroLiveIframe;
